import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/auth";
import FileUploader from "@/components/FileUploader";
import { toast } from "sonner";

const PRICE = 79;

const MOODS = ["Sobre","Élégante","Moderne","Minimaliste","Mystérieuse","Sombre","Lumineuse","Poétique","Dynamique","Émotionnelle","Luxueuse","Vintage","Autre"];
const GENRES = ["Roman","Thriller / Policier","Science-fiction / Fantasy","Romance","Témoignage / Biographie","Essai","Spiritualité","Développement personnel","Jeunesse","Poésie","Guide / Pratique","Autre"];

const STEPS = ["Votre livre", "Univers graphique", "Vos éléments", "Format & livraison", "Récapitulatif"];

export default function CouvertureConfigurator() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Book
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [author, setAuthor] = useState("");
  const [editionStatus, setEditionStatus] = useState(""); // "publisher"|"self"|"undecided"
  const [publisherName, setPublisherName] = useState("");
  const [publisherLogoDisplay, setPublisherLogoDisplay] = useState(""); // yes/no/unsure
  const [publisherLogoFiles, setPublisherLogoFiles] = useState([]);
  const [publisherLogoPos, setPublisherLogoPos] = useState("");
  const [publisherLogoPosOther, setPublisherLogoPosOther] = useState("");
  const [genre, setGenre] = useState("");
  const [genreOther, setGenreOther] = useState("");
  const [synopsis, setSynopsis] = useState("");
  const [audience, setAudience] = useState("");

  // Style
  const [moods, setMoods] = useState([]);
  const [moodOther, setMoodOther] = useState("");
  const [ideaPrecision, setIdeaPrecision] = useState("");
  const [elementsWanted, setElementsWanted] = useState("");
  const [elementsAvoided, setElementsAvoided] = useState("");
  const [colors, setColors] = useState("");
  const [imageSource, setImageSource] = useState("");

  // Elements
  const [elementFiles, setElementFiles] = useState([]);

  // Format
  const [widthMm, setWidthMm] = useState("");
  const [heightMm, setHeightMm] = useState("");
  const [dimsUnknown, setDimsUnknown] = useState(false);
  const [destinations, setDestinations] = useState([]);
  const [printerPlatform, setPrinterPlatform] = useState("");
  const [printerSpecsStatus, setPrinterSpecsStatus] = useState("unsure");
  const [printerSpecsNotes, setPrinterSpecsNotes] = useState("");
  const [printerSpecsFiles, setPrinterSpecsFiles] = useState([]);
  const [deliverables, setDeliverables] = useState([]);

  const [confirmChk, setConfirmChk] = useState(false);

  const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);

  const canNext = useMemo(() => {
    if (step === 0) {
      if (!title.trim() || !author.trim() || !synopsis.trim() || !editionStatus || !genre) return false;
      if (genre === "Autre" && !genreOther.trim()) return false;
      if (editionStatus === "publisher") {
        if (!publisherName.trim() || !publisherLogoDisplay) return false;
        if (publisherLogoDisplay === "yes" && !publisherLogoPos) return false;
      }
      return true;
    }
    if (step === 1) return moods.length > 0 && ideaPrecision && imageSource;
    if (step === 2) return true; // fichiers optionnels
    if (step === 3) {
      if (!dimsUnknown && (!widthMm || !heightMm)) return false;
      if (destinations.length === 0) return false;
      if (deliverables.length === 0) return false;
      return true;
    }
    return true;
  }, [step, title, author, synopsis, editionStatus, genre, genreOther, publisherName, publisherLogoDisplay, publisherLogoPos, moods, ideaPrecision, imageSource, dimsUnknown, widthMm, heightMm, destinations, deliverables]);

  const submit = async () => {
    if (!confirmChk) { toast.error("Cochez la confirmation pour envoyer votre commande."); return; }
    setSubmitting(true);
    try {
      const payload = {
        book: {
          title: title.trim(), subtitle: subtitle.trim(), author: author.trim(),
          edition_status: editionStatus,
          publisher_name: publisherName.trim(),
          publisher_logo_display: editionStatus === "publisher" ? publisherLogoDisplay : null,
          publisher_logo_file_ids: publisherLogoFiles.map(f => f.id).filter(Boolean),
          publisher_logo_position: publisherLogoDisplay === "yes" ? publisherLogoPos : null,
          publisher_logo_position_other: publisherLogoPosOther.trim(),
          genre, genre_other: genre === "Autre" ? genreOther.trim() : "",
          synopsis: synopsis.trim(), audience: audience.trim(),
        },
        style: {
          moods, mood_other: moods.includes("Autre") ? moodOther.trim() : "",
          idea_precision: ideaPrecision,
          elements_wanted: elementsWanted.trim(), elements_avoided: elementsAvoided.trim(),
          colors: colors.trim(),
          image_source: imageSource,
        },
        format: {
          width_mm: dimsUnknown ? null : Number(widthMm) || null,
          height_mm: dimsUnknown ? null : Number(heightMm) || null,
          dimensions_unknown: dimsUnknown,
          destinations,
          printer_platform: printerPlatform.trim(),
          printer_specs_status: printerSpecsStatus,
          printer_specs_notes: printerSpecsNotes.trim(),
          printer_specs_file_ids: printerSpecsFiles.map(f => f.id).filter(Boolean),
          deliverables,
        },
        element_file_ids: elementFiles.map(f => f.id).filter(Boolean),
        confirm: true,
      };
      const { data } = await api.post("/couverture/order", payload);
      toast.success("Commande enregistrée");
      nav(`/espace/commande/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSubmitting(false); }
  };

  const Radio = ({ name, value, checked, onChange, label, testid }) => (
    <label className={`flex items-start gap-2 px-3 py-2 border cursor-pointer text-[14px] ${checked ? "border-[#0E7C90] bg-[#0E7C90]/5" : "border-black/20 bg-white"}`}>
      <input type="radio" name={name} checked={checked} onChange={onChange} data-testid={testid} className="mt-1" />
      <span>{label}</span>
    </label>
  );
  const Chk = ({ checked, onChange, label, testid }) => (
    <label className={`flex items-start gap-2 px-3 py-2 border cursor-pointer text-[13px] ${checked ? "border-[#0E7C90] bg-[#0E7C90]/5" : "border-black/20 bg-white"}`}>
      <input type="checkbox" checked={checked} onChange={onChange} data-testid={testid} className="mt-1" />
      <span>{label}</span>
    </label>
  );

  return (
    <div className="cs-cream min-h-[85vh] py-10" data-testid="page-couverture-configurator">
      <div className="max-w-[1000px] mx-auto px-6 lg:px-12">
        <Link to="/services/couvertures" className="text-sm font-mono uppercase tracking-[0.16em] text-neutral-500 hover:text-black inline-flex items-center gap-2">
          <ArrowLeft size={14} /> Retour
        </Link>
        <div className="mt-4">
          <div className="cs-eyebrow text-[#0E7C90]">Couvertures · Livre</div>
          <h1 className="cs-h1 mt-2 text-3xl lg:text-5xl">Première de couverture — 79 €</h1>
          <p className="mt-3 text-neutral-700 text-[15px] leading-relaxed max-w-2xl">
            Création graphique de la face avant de votre livre : composition, image, titre et nom d'auteur, prête pour l'impression.
            <br /><span className="text-[13px] text-neutral-500">Pour une couverture comprenant également le dos, la tranche et la quatrième de couverture, choisissez <Link to="/prestation/couverture-complete" className="cs-link-underline">« Couverture complète »</Link>.</span>
          </p>
        </div>

        <div className="mt-6 flex items-center gap-2 flex-wrap">
          {STEPS.map((s, i) => (
            <div key={i} className={`flex items-center gap-2 text-[12px] font-mono uppercase tracking-widest ${i === step ? "text-[#0E7C90]" : i < step ? "text-neutral-800" : "text-neutral-400"}`}>
              <span className={`inline-flex items-center justify-center w-6 h-6 border ${i === step ? "border-[#0E7C90] bg-[#0E7C90]/10" : i < step ? "border-black/50 bg-black text-white" : "border-black/20"}`}>{i < step ? <Check size={12} /> : i + 1}</span>
              <span className="hidden sm:inline">{s}</span>
            </div>
          ))}
        </div>

        <div className="mt-8 space-y-6">
          {step === 0 && (
            <section className="cs-card p-6 space-y-5" data-testid="step-book">
              <h2 className="cs-h3 text-2xl">Votre livre</h2>
              <label className="block"><span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Titre du livre *</span>
                <input required value={title} onChange={e => setTitle(e.target.value)} className="cs-input mt-2 w-full" data-testid="couv-title" /></label>
              <label className="block"><span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Sous-titre</span>
                <input value={subtitle} onChange={e => setSubtitle(e.target.value)} className="cs-input mt-2 w-full" data-testid="couv-subtitle" /></label>
              <label className="block"><span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Nom de l'auteur / nom à afficher *</span>
                <input required value={author} onChange={e => setAuthor(e.target.value)} className="cs-input mt-2 w-full" data-testid="couv-author" />
                <span className="block text-[12px] text-neutral-500 mt-1">Indiquez exactement l'orthographe et la présentation souhaitées.</span></label>

              <div>
                <span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Situation éditoriale *</span>
                <div className="mt-2 grid sm:grid-cols-3 gap-2">
                  <Radio name="ed" checked={editionStatus === "publisher"} onChange={() => setEditionStatus("publisher")} label="Publié par une maison d'édition" testid="couv-ed-publisher" />
                  <Radio name="ed" checked={editionStatus === "self"} onChange={() => setEditionStatus("self")} label="Autoédition" testid="couv-ed-self" />
                  <Radio name="ed" checked={editionStatus === "undecided"} onChange={() => setEditionStatus("undecided")} label="Pas encore défini" testid="couv-ed-undecided" />
                </div>
              </div>

              {editionStatus === "publisher" && (
                <div className="space-y-4 border-l-2 border-[#0E7C90] pl-4">
                  <label className="block"><span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Nom de la maison d'édition *</span>
                    <input required value={publisherName} onChange={e => setPublisherName(e.target.value)} className="cs-input mt-2 w-full" data-testid="couv-publisher-name" /></label>
                  <div>
                    <span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Le logo de l'éditeur doit-il apparaître sur la première de couverture ?</span>
                    <div className="mt-2 grid sm:grid-cols-3 gap-2">
                      <Radio name="pl" checked={publisherLogoDisplay === "yes"} onChange={() => setPublisherLogoDisplay("yes")} label="Oui" testid="couv-logo-yes" />
                      <Radio name="pl" checked={publisherLogoDisplay === "no"} onChange={() => setPublisherLogoDisplay("no")} label="Non" testid="couv-logo-no" />
                      <Radio name="pl" checked={publisherLogoDisplay === "unsure"} onChange={() => setPublisherLogoDisplay("unsure")} label="Je ne sais pas encore" testid="couv-logo-unsure" />
                    </div>
                  </div>
                  {publisherLogoDisplay === "yes" && (
                    <>
                      <div>
                        <span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Logo de l'éditeur</span>
                        <p className="text-[12px] text-neutral-500 mt-1 mb-2">Transmettez de préférence le logo en PNG avec fond transparent.</p>
                        <FileUploader files={publisherLogoFiles} setFiles={setPublisherLogoFiles} testId="couv-logo-upload" />
                      </div>
                      <div>
                        <span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Position souhaitée du logo</span>
                        <div className="mt-2 grid sm:grid-cols-2 gap-2">
                          {[{k:"bottom-left",l:"En bas à gauche"},{k:"bottom-center",l:"En bas au centre"},{k:"bottom-right",l:"En bas à droite"},{k:"other",l:"Autre emplacement"},{k:"studio",l:"Je laisse Charbonier Studio choisir"}].map(o =>
                            <Radio key={o.k} name="lp" checked={publisherLogoPos === o.k} onChange={() => setPublisherLogoPos(o.k)} label={o.l} testid={`couv-logopos-${o.k}`} />
                          )}
                        </div>
                        {publisherLogoPos === "other" && (
                          <input value={publisherLogoPosOther} onChange={e => setPublisherLogoPosOther(e.target.value)} placeholder="Précisez…" className="cs-input mt-2 w-full" data-testid="couv-logopos-other" />
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              <div>
                <span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Genre / type d'ouvrage *</span>
                <select value={genre} onChange={e => setGenre(e.target.value)} className="cs-input mt-2 w-full" data-testid="couv-genre">
                  <option value="">— Choisir —</option>
                  {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
                {genre === "Autre" && <input value={genreOther} onChange={e => setGenreOther(e.target.value)} placeholder="Précisez…" className="cs-input mt-2 w-full" data-testid="couv-genre-other" />}
              </div>

              <label className="block"><span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">En quelques lignes, de quoi parle votre livre ? *</span>
                <textarea rows={5} value={synopsis} onChange={e => setSynopsis(e.target.value)} className="cs-input mt-2 w-full resize-none" data-testid="couv-synopsis" placeholder="Résumez librement l'histoire, le sujet ou l'idée principale de votre ouvrage. Quelques phrases suffisent." /></label>
              <label className="block"><span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">À qui s'adresse principalement ce livre ?</span>
                <input value={audience} onChange={e => setAudience(e.target.value)} placeholder="Ex. : grand public, adolescents, passionnés d'un domaine…" className="cs-input mt-2 w-full" data-testid="couv-audience" /></label>
            </section>
          )}

          {step === 1 && (
            <section className="cs-card p-6 space-y-5" data-testid="step-style">
              <h2 className="cs-h3 text-2xl">Univers graphique</h2>
              <div>
                <span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Quelle ambiance souhaitez-vous transmettre ? (plusieurs choix possibles)</span>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {MOODS.map(m => <Chk key={m} checked={moods.includes(m)} onChange={() => toggle(moods, setMoods, m)} label={m} testid={`couv-mood-${m}`} />)}
                </div>
                {moods.includes("Autre") && <input value={moodOther} onChange={e => setMoodOther(e.target.value)} placeholder="Précisez…" className="cs-input mt-2 w-full" data-testid="couv-mood-other" />}
              </div>
              <div>
                <span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Où en êtes-vous dans votre idée de couverture ? *</span>
                <div className="mt-2 grid gap-2">
                  <Radio name="ip" checked={ideaPrecision === "precise"} onChange={() => setIdeaPrecision("precise")} label="J'ai une idée assez précise" testid="couv-idea-precise" />
                  <Radio name="ip" checked={ideaPrecision === "some"} onChange={() => setIdeaPrecision("some")} label="J'ai quelques pistes mais je souhaite être conseillé" testid="couv-idea-some" />
                  <Radio name="ip" checked={ideaPrecision === "none"} onChange={() => setIdeaPrecision("none")} label="Je n'ai pas d'idée précise, je préfère laisser Charbonier Studio proposer" testid="couv-idea-none" />
                </div>
              </div>
              <label className="block"><span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Qu'aimeriez-vous voir sur la couverture ?</span>
                <textarea rows={3} value={elementsWanted} onChange={e => setElementsWanted(e.target.value)} className="cs-input mt-2 w-full resize-none" data-testid="couv-elements-wanted" placeholder="Personnage, paysage, objet, symbole, photographie, illustration, composition abstraite…" /></label>
              <label className="block"><span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Y a-t-il des éléments à ne surtout pas voir ?</span>
                <textarea rows={2} value={elementsAvoided} onChange={e => setElementsAvoided(e.target.value)} className="cs-input mt-2 w-full resize-none" data-testid="couv-elements-avoided" /></label>
              <label className="block"><span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Couleurs souhaitées ou à éviter</span>
                <input value={colors} onChange={e => setColors(e.target.value)} className="cs-input mt-2 w-full" data-testid="couv-colors" placeholder="Ex. : plutôt des tons sombres, éviter le rouge…" /></label>
              <div>
                <span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Concernant l'image principale *</span>
                <div className="mt-2 grid gap-2">
                  <Radio name="is" checked={imageSource === "client"} onChange={() => setImageSource("client")} label="Je fournis une image" testid="couv-img-client" />
                  <Radio name="is" checked={imageSource === "studio"} onChange={() => setImageSource("studio")} label="Je souhaite que Charbonier Studio recherche un visuel adapté" testid="couv-img-studio" />
                  <Radio name="is" checked={imageSource === "unsure"} onChange={() => setImageSource("unsure")} label="Je ne sais pas encore / je souhaite être conseillé" testid="couv-img-unsure" />
                </div>
                <p className="mt-2 text-[12px] text-neutral-500 leading-relaxed">La recherche iconographique est comprise dans les 79 €. Les éventuels achats de photographies, illustrations, polices ou autres ressources sous licence payante ne sont pas compris dans le tarif. Aucun achat ne sera effectué sans votre accord préalable.</p>
              </div>
            </section>
          )}

          {step === 2 && (
            <section className="cs-card p-6 space-y-4" data-testid="step-elements">
              <h2 className="cs-h3 text-2xl">Vos éléments</h2>
              <p className="text-[14px] text-neutral-700">Transmettez les éléments utiles à la création de votre couverture : photographie ou image à utiliser, logo de l'éditeur, croquis, exemple de couverture appréciée, document présentant le livre, ou consignes complémentaires.</p>
              <FileUploader files={elementFiles} setFiles={setElementFiles} testId="couv-elements-upload" />
              <p className="text-[12px] text-neutral-500">Vous pouvez transmettre des couvertures existantes comme références d'ambiance ou de style. Elles ne seront pas reproduites à l'identique.</p>
            </section>
          )}

          {step === 3 && (
            <section className="cs-card p-6 space-y-5" data-testid="step-format">
              <h2 className="cs-h3 text-2xl">Format & livraison</h2>
              <div>
                <span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Dimensions finales (mm)</span>
                <div className="mt-2 flex gap-3 items-center flex-wrap">
                  <input type="number" min="1" value={widthMm} onChange={e => setWidthMm(e.target.value)} placeholder="Largeur" disabled={dimsUnknown} className="cs-input w-32 disabled:opacity-50" data-testid="couv-width" />
                  <span>×</span>
                  <input type="number" min="1" value={heightMm} onChange={e => setHeightMm(e.target.value)} placeholder="Hauteur" disabled={dimsUnknown} className="cs-input w-32 disabled:opacity-50" data-testid="couv-height" />
                  <span className="text-[12px] text-neutral-500">Ex. : 148 × 210 mm</span>
                </div>
                <Chk checked={dimsUnknown} onChange={() => setDimsUnknown(v => !v)} label="Je ne connais pas encore les dimensions exactes" testid="couv-dims-unknown" />
              </div>
              <div>
                <span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Destination *</span>
                <div className="mt-2 grid sm:grid-cols-3 gap-2">
                  <Chk checked={destinations.includes("print")} onChange={() => toggle(destinations, setDestinations, "print")} label="Impression" testid="couv-dest-print" />
                  <Chk checked={destinations.includes("digital")} onChange={() => toggle(destinations, setDestinations, "digital")} label="Version numérique" testid="couv-dest-digital" />
                  <Chk checked={destinations.includes("both")} onChange={() => toggle(destinations, setDestinations, "both")} label="Les deux" testid="couv-dest-both" />
                </div>
              </div>
              <label className="block"><span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Imprimeur / plateforme (facultatif)</span>
                <input value={printerPlatform} onChange={e => setPrinterPlatform(e.target.value)} placeholder="Ex. : Amazon KDP, Bookelis, BoD, imprimeur local…" className="cs-input mt-2 w-full" data-testid="couv-printer" /></label>
              <div>
                <span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Votre imprimeur vous a-t-il fourni des spécifications ?</span>
                <div className="mt-2 grid sm:grid-cols-3 gap-2">
                  <Radio name="ps" checked={printerSpecsStatus === "yes"} onChange={() => setPrinterSpecsStatus("yes")} label="Oui" testid="couv-specs-yes" />
                  <Radio name="ps" checked={printerSpecsStatus === "no"} onChange={() => setPrinterSpecsStatus("no")} label="Non" testid="couv-specs-no" />
                  <Radio name="ps" checked={printerSpecsStatus === "unsure"} onChange={() => setPrinterSpecsStatus("unsure")} label="Je ne sais pas" testid="couv-specs-unsure" />
                </div>
                {printerSpecsStatus === "yes" && (
                  <div className="mt-3 space-y-3 border-l-2 border-[#0E7C90] pl-4">
                    <label className="block"><span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Consignes de l'imprimeur</span>
                      <textarea rows={3} value={printerSpecsNotes} onChange={e => setPrinterSpecsNotes(e.target.value)} className="cs-input mt-2 w-full resize-none" data-testid="couv-specs-notes" placeholder="Collez ici les spécifications reçues." /></label>
                    <div><span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Document technique / Joboptions</span>
                      <FileUploader files={printerSpecsFiles} setFiles={setPrinterSpecsFiles} testId="couv-specs-files" /></div>
                  </div>
                )}
              </div>
              <div>
                <span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Fichiers à livrer * (au moins un choix — plusieurs possibles, sans supplément si mêmes dimensions)</span>
                <div className="mt-2 space-y-2">
                  <label className={`block p-3 border cursor-pointer ${deliverables.includes("psd_print") ? "border-[#0E7C90] bg-[#0E7C90]/5" : "border-black/20 bg-white"}`}>
                    <input type="checkbox" checked={deliverables.includes("psd_print")} onChange={() => toggle(deliverables, setDeliverables, "psd_print")} data-testid="couv-del-psd" />
                    <span className="ml-2 font-medium">PSD impression</span>
                    <span className="block text-[12px] text-neutral-500 ml-6">PSD aplati — CMJN — 300 ppp — dimensions finales, prêt à intégrer dans votre maquette. (Les calques de travail ne sont pas inclus.)</span>
                  </label>
                  <label className={`block p-3 border cursor-pointer ${deliverables.includes("digital") ? "border-[#0E7C90] bg-[#0E7C90]/5" : "border-black/20 bg-white"}`}>
                    <input type="checkbox" checked={deliverables.includes("digital")} onChange={() => toggle(deliverables, setDeliverables, "digital")} data-testid="couv-del-digital" />
                    <span className="ml-2 font-medium">Version numérique</span>
                    <span className="block text-[12px] text-neutral-500 ml-6">Fichier RVB optimisé pour une utilisation numérique / écran.</span>
                  </label>
                  <label className={`block p-3 border cursor-pointer ${deliverables.includes("pdf_print") ? "border-[#0E7C90] bg-[#0E7C90]/5" : "border-black/20 bg-white"}`}>
                    <input type="checkbox" checked={deliverables.includes("pdf_print")} onChange={() => toggle(deliverables, setDeliverables, "pdf_print")} data-testid="couv-del-pdf" />
                    <span className="ml-2 font-medium">PDF prêt imprimeur</span>
                    <span className="block text-[12px] text-neutral-500 ml-6">PDF conforme aux spécifications communiquées : format, fond perdu, profil colorimétrique, paramètres PDF/Joboptions et traits de coupe lorsqu'ils sont requis par l'imprimeur.</span>
                  </label>
                </div>
                <p className="mt-2 text-[12px] text-neutral-500">Une adaptation de la création à un autre format constitue une prestation distincte.</p>
              </div>
            </section>
          )}

          {step === 4 && (
            <section className="cs-card p-6 space-y-5" data-testid="step-summary">
              <h2 className="cs-h3 text-2xl">Récapitulatif</h2>
              <SummaryBlock title="Prestation">Première de couverture</SummaryBlock>
              <SummaryBlock title="Livre">
                <SumRow k="Titre" v={title} />
                {subtitle && <SumRow k="Sous-titre" v={subtitle} />}
                <SumRow k="Auteur" v={author} />
                <SumRow k="Genre" v={genre === "Autre" ? genreOther : genre} />
                {audience && <SumRow k="Public" v={audience} />}
                <SumRow k="Situation" v={editionStatus === "publisher" ? "Publié par une maison d'édition" : editionStatus === "self" ? "Autoédition" : "Pas encore défini"} />
                {editionStatus === "publisher" && (<>
                  <SumRow k="Éditeur" v={publisherName} />
                  <SumRow k="Logo éditeur" v={publisherLogoDisplay === "yes" ? `Oui — ${publisherLogoPos}${publisherLogoPos === "other" ? " ("+publisherLogoPosOther+")" : ""}` : publisherLogoDisplay === "no" ? "Non" : "À décider"} />
                </>)}
              </SummaryBlock>
              <SummaryBlock title="Direction graphique">
                <SumRow k="Ambiances" v={moods.join(", ") + (moods.includes("Autre") && moodOther ? " ("+moodOther+")" : "")} />
                <SumRow k="Précision" v={ideaPrecision === "precise" ? "Idée précise" : ideaPrecision === "some" ? "Quelques pistes" : "Direction laissée au studio"} />
                {elementsWanted && <SumRow k="Éléments souhaités" v={elementsWanted} />}
                {elementsAvoided && <SumRow k="À éviter" v={elementsAvoided} />}
                {colors && <SumRow k="Couleurs" v={colors} />}
                <SumRow k="Image" v={imageSource === "client" ? "Fournie par le client" : imageSource === "studio" ? "Recherche par le studio" : "À décider"} />
              </SummaryBlock>
              {elementFiles.length > 0 && (
                <SummaryBlock title="Fichiers transmis">
                  <ul className="text-[13px] space-y-1">{elementFiles.map(f => <li key={f.id}>• {f.original_name}</li>)}</ul>
                </SummaryBlock>
              )}
              <SummaryBlock title="Format & livraison">
                <SumRow k="Dimensions" v={dimsUnknown ? "À définir" : `${widthMm} × ${heightMm} mm`} />
                <SumRow k="Destination" v={destinations.map(d => d === "print" ? "Impression" : d === "digital" ? "Numérique" : "Les deux").join(", ")} />
                {printerPlatform && <SumRow k="Imprimeur" v={printerPlatform} />}
                {printerSpecsStatus === "yes" && printerSpecsNotes && <SumRow k="Consignes" v={printerSpecsNotes.slice(0, 200)+(printerSpecsNotes.length > 200 ? "…" : "")} />}
                <SumRow k="Livrables" v={deliverables.map(d => d === "psd_print" ? "PSD impression" : d === "digital" ? "Numérique" : "PDF prêt imprimeur").join(", ")} />
              </SummaryBlock>
              <SummaryBlock title="Délai estimatif">5 à 7 jours ouvrés</SummaryBlock>

              <div className="p-4 bg-neutral-50 border border-black/10 text-[12px] text-neutral-600 leading-relaxed">
                <strong>Inclus dans les 79 €</strong> : conception graphique complète, recherche iconographique, composition, typographie, traitement des images, intégration titre/sous-titre/auteur/logo éditeur, 1 proposition initiale, <strong>1 réorientation complète incluse</strong>, <strong>2 groupes de corrections légères</strong> (un groupe = un ensemble de modifications transmis en une seule fois), fichiers finaux sélectionnés. Groupe supplémentaire : +19 €. Nouvelle refonte complète : +39 €. Ces suppléments ne sont jamais prélevés sans votre accord préalable.
              </div>

              <label className="flex items-start gap-2 text-[13px]">
                <input type="checkbox" checked={confirmChk} onChange={e => setConfirmChk(e.target.checked)} data-testid="couv-confirm" className="mt-1" />
                <span>Je confirme que les informations transmises correspondent à ma demande.</span>
              </label>
              <div className="text-2xl font-serif" data-testid="couv-total">Total : {PRICE} €</div>
              <button onClick={submit} disabled={!confirmChk || submitting} data-testid="couv-submit" className="cs-btn-primary disabled:opacity-50 inline-flex items-center gap-2">
                {submitting ? "Envoi…" : "Confirmer la commande"} <ArrowUpRight size={16} />
              </button>
            </section>
          )}
        </div>

        <div className="mt-8 flex items-center justify-between">
          <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0 || submitting} className="cs-btn-ghost !text-black !border-black disabled:opacity-30" data-testid="couv-prev">
            ← Précédent
          </button>
          {step < STEPS.length - 1 && (
            <button onClick={() => setStep(s => s + 1)} disabled={!canNext} className="cs-btn-primary disabled:opacity-50" data-testid="couv-next">
              Suivant →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const SummaryBlock = ({ title, children }) => (
  <div>
    <div className="cs-eyebrow text-neutral-500 mb-2">{title}</div>
    <div className="text-[14px] space-y-1">{children}</div>
  </div>
);
const SumRow = ({ k, v }) => (
  <div className="grid grid-cols-[140px_1fr] gap-3"><span className="text-neutral-500">{k}</span><span className="text-neutral-800">{v || "—"}</span></div>
);
